import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import NewsCell from './NewsCell';
import { RootState } from '../redux/store';
import { useSelector } from '../redux/hooks';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background: #ffffff;
`;

const Title = styled.h1`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 32px;
  color: #a13b16;
  text-align: center;
  margin-bottom: 30px;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const NewsGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  align-content: start;
`;

const FormPanel = styled.div`
  width: 300px;
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    width: 100%;
    position: static;
  }
`;

const FormCard = styled.form`
  padding: 20px;
  border-radius: 8px;
  background: #f9f9f9;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputField = styled.input`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  transition: border 0.2s;

  &:focus {
    border-color: #a13b16;
    outline: none;
  }
`;

const TextAreaField = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  width: 100%;
  transition: border 0.2s;

  &:focus {
    border-color: #a13b16;
    outline: none;
  }
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 6px;
  background: #a13b16;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 14px;

  &:hover {
    background: #8b2f0f;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(Button)`
  background: #c0392b;

  &:hover {
    background: #a93226;
  }
`;

const SecondaryButton = styled(Button)`
  background: #666;

  &:hover {
    background: #555;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

interface News {
  news_id: number;
  news_title: string;
  news_description: string;
  news_cover_path: string;
  news_link: string;
}

const BASE_URL = process.env.REACT_APP_BASE_API_URL

const NewsListView: React.FC = () => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const navigate = useNavigate();

  const [newsList, setNewsList] = useState<News[]>([]);

  const [selectedNews, setSelectedNews] = useState<News | null>(null);
  const [titleInput, setTitleInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const res = await fetch(`${BASE_URL}/news`);
      const data = await res.json();
      setNewsList(data);
    } catch (err) {
      console.error('Помилка при завантаженні новин', err);
    }
  };

  const handleNewsClick = useCallback((news: News) => {
    setSelectedNews(news);
    setTitleInput(news.news_title);
    setDescInput(news.news_description);
    setLinkInput(news.news_link);
    setImagePreview(news.news_cover_path);
    setImageFile(null);
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titleInput.trim() || !descInput.trim()) return;

    const formData = new FormData();
    formData.append('news_title', titleInput);
    formData.append('news_description', descInput);
    formData.append('news_link', linkInput);
    if (imageFile) {
      const fileKey = 'news_cover_path';
      formData.append(fileKey, imageFile);
    }

    try {
      let res;
      if (selectedNews) {
        res = await fetch(`${BASE_URL}/news/${selectedNews.news_id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });
      } else {
        res = await fetch(`${BASE_URL}/news`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        });
      }

      if (!res.ok) throw new Error('Помилка запиту');
      await fetchNews();
      resetForm();
      alert('Новину додано');
    } catch (err) {
      console.error('Помилка при збереженні новини', err);
    }
  }, [titleInput, descInput, imageFile, selectedNews, accessToken]);

  const handleDelete = useCallback(async () => {
    if (!selectedNews) return;

    try {
      const res = await fetch(`${BASE_URL}/news/${selectedNews.news_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) throw new Error('Помилка видалення');

      setNewsList(prev => prev.filter(n => n.news_id !== selectedNews.news_id));
      resetForm();
    } catch (err) {
      console.error('Помилка при видаленні новини', err);
    }
  }, [accessToken, selectedNews]);

  const resetForm = () => {
    setSelectedNews(null);
    setTitleInput('');
    setDescInput('');
    setLinkInput('');
    setImageFile(null);
    setImagePreview(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <PageContainer>
      <TopBar>
        <Title>Новини</Title>
        <Button onClick={() => navigate('/admin')}>Головна</Button>
      </TopBar>
      
      <ContentWrapper>
        <NewsGrid>
          {newsList.map(news => (
            <NewsCell
              key={news.news_id}
              news={news}
              onNewsClick={handleNewsClick}
            />
          ))}
        </NewsGrid>

        <FormPanel>
          <FormCard onSubmit={handleSubmit}>
            <InputField
              placeholder="Заголовок новини"
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              required
            />

            <TextAreaField
              placeholder="Опис новини"
              value={descInput}
              onChange={(e) => setDescInput(e.target.value)}
              required
            />

            <InputField
              placeholder="Посилання"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              required
            />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setImageFile(file);
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setImagePreview(reader.result as string);
                  };
                  reader.readAsDataURL(file);
                } else {
                  setImagePreview(null);
                }
              }}
            />

            {imagePreview && (
              <img
                src={imagePreview}
                alt="Превʼю зображення"
                style={{ maxWidth: '100%', borderRadius: '6px' }}
              />
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Button type="submit" disabled={!titleInput || !descInput}>
                {selectedNews ? 'Оновити новину' : 'Додати новину'}
              </Button>

              {selectedNews && (
                <>
                  <DangerButton type="button" onClick={handleDelete}>
                    Видалити новину
                  </DangerButton>
                  <SecondaryButton type="button" onClick={resetForm}>
                    Скасувати
                  </SecondaryButton>
                </>
              )}
            </div>
          </FormCard>
        </FormPanel>
      </ContentWrapper>
    </PageContainer>
  );
};

export default NewsListView;